import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Award, 
  Star, 
  TrendingUp, 
  Gift, 
  Clock, 
  ChevronRight,
  Sparkles,
  Crown,
  Medal,
  Trophy
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import LoyaltyBadge from '../components/LoyaltyBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Loyalty = () => {
  const { token, loyaltyPoints, totalOrders } = useAuth();
  const [balance, setBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [tiers, setTiers] = useState(null);
  const [activeCampaign, setActiveCampaign] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLoyaltyData();
  }, []);

  const fetchLoyaltyData = async () => {
    try {
      const [balanceRes, transactionsRes, tiersRes, campaignRes] = await Promise.all([
        axios.get(`${API}/loyalty/balance`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/loyalty/transactions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/loyalty/tiers`),
        axios.get(`${API}/loyalty/active-campaign`)
      ]);
      setBalance(balanceRes.data);
      setTransactions(transactionsRes.data);
      setTiers(tiersRes.data);
      setActiveCampaign(campaignRes.data);
    } catch (error) {
      console.error('Loyalty data fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCurrentTier = () => {
    if (!tiers) return null;
    const orders = totalOrders;
    let currentTier = tiers.tiers[0];
    for (const tier of tiers.tiers) {
      if (orders >= tier.min_orders) {
        currentTier = tier;
      }
    }
    return currentTier;
  };

  const getNextTier = () => {
    if (!tiers) return null;
    const orders = totalOrders;
    for (const tier of tiers.tiers) {
      if (orders < tier.min_orders) {
        return tier;
      }
    }
    return null;
  };

  const getTierIcon = (tierName) => {
    switch (tierName?.toLowerCase()) {
      case 'bronz': return Medal;
      case 'gümüş': return Star;
      case 'altın': return Trophy;
      case 'platin': return Crown;
      default: return Award;
    }
  };

  const getTierColor = (tierName) => {
    switch (tierName?.toLowerCase()) {
      case 'bronz': return 'text-amber-700 bg-amber-100';
      case 'gümüş': return 'text-gray-600 bg-gray-200';
      case 'altın': return 'text-yellow-600 bg-yellow-100';
      case 'platin': return 'text-purple-600 bg-purple-100';
      default: return 'text-charcoal bg-stone-100';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-terracotta border-t-transparent" />
      </div>
    );
  }

  const currentTier = getCurrentTier();
  const nextTier = getNextTier();
  const TierIcon = getTierIcon(currentTier?.name);

  return (
    <div className="min-h-screen bg-bone pb-8">
      {/* Header */}
      <div className="bg-gradient-to-br from-basil to-basil/80 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full text-white/90 text-sm font-medium mb-4">
              <Sparkles className="w-4 h-4" />
              Sadakat Programı
            </div>
            <h1 className="font-playfair text-4xl md:text-5xl font-bold text-white mb-2">
              Puanlarınız
            </h1>
            <div className="font-playfair text-6xl md:text-7xl font-bold text-white mt-4">
              {loyaltyPoints}
              <span className="text-2xl ml-2 font-manrope font-normal opacity-80">puan</span>
            </div>
            {balance && (
              <p className="text-white/80 mt-2">
                ≈ €{balance.points_value.toFixed(2)} değerinde
              </p>
            )}

            {/* Tier badge — backend tier card below shows progress, so keep this badge as a chip only */}
            <div className="mt-6 flex items-center justify-center">
              <LoyaltyBadge totalOrders={totalOrders || 0} size="lg" variant="pill" />
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Active Campaign Banner */}
        {activeCampaign && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 bg-gradient-to-r from-terracotta to-terracotta/80 rounded-2xl p-6 text-white"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                  <Sparkles className="w-7 h-7" />
                </div>
                <div>
                  <p className="text-white/80 text-sm">Aktif Kampanya</p>
                  <h3 className="font-playfair text-2xl font-bold">{activeCampaign.name}</h3>
                  {activeCampaign.description && (
                    <p className="text-white/80 text-sm mt-1">{activeCampaign.description}</p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <span className="inline-block px-4 py-2 bg-white/20 rounded-full text-xl font-bold">
                  {activeCampaign.bonus_multiplier}x PUAN
                </span>
                <p className="text-white/70 text-sm mt-2">
                  {format(new Date(activeCampaign.end_date), "d MMM'e kadar", { locale: tr })}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Current Tier Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-playfair text-xl font-semibold text-charcoal">
                  Üyelik Seviyeniz
                </h2>
                <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${getTierColor(currentTier?.name)}`}>
                  <TierIcon className="w-5 h-5" />
                  <span className="font-semibold">{currentTier?.name}</span>
                </div>
              </div>

              {/* Progress to next tier */}
              {nextTier && (
                <div className="mb-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-charcoal/60">Sonraki seviye: {nextTier.name}</span>
                    <span className="text-charcoal font-medium">
                      {totalOrders} / {nextTier.min_orders} sipariş
                    </span>
                  </div>
                  <div className="h-3 bg-stone-200 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${Math.min((totalOrders / nextTier.min_orders) * 100, 100)}%` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                      className="h-full bg-gradient-to-r from-terracotta to-wheat rounded-full"
                    />
                  </div>
                  <p className="text-sm text-charcoal/60 mt-2">
                    {nextTier.min_orders - totalOrders} sipariş daha verin, <span className="text-terracotta font-semibold">{nextTier.name}</span> seviyesine yükselin!
                  </p>
                </div>
              )}

              {/* Current tier benefits */}
              <div>
                <h3 className="font-medium text-charcoal mb-3">Avantajlarınız</h3>
                <div className="space-y-2">
                  {tiers?.benefits[currentTier?.icon]?.map((benefit, index) => (
                    <div key={index} className="flex items-center gap-3 text-charcoal/70">
                      <div className="w-6 h-6 bg-basil/10 rounded-full flex items-center justify-center">
                        <Gift className="w-3 h-3 text-basil" />
                      </div>
                      {benefit}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Transactions */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              <div className="flex items-center gap-3 mb-6">
                <Clock className="w-5 h-5 text-terracotta" />
                <h2 className="font-playfair text-xl font-semibold text-charcoal">
                  Puan Geçmişi
                </h2>
              </div>

              {transactions.length > 0 ? (
                <div className="space-y-4">
                  {transactions.map((tx) => (
                    <div
                      key={tx.id}
                      className="flex items-center justify-between py-3 border-b border-stone-100 last:border-0"
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          tx.points > 0 ? 'bg-green-100' : 'bg-red-100'
                        }`}>
                          {tx.points > 0 ? (
                            <TrendingUp className="w-5 h-5 text-green-600" />
                          ) : (
                            <Gift className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-charcoal">{tx.description}</p>
                          <p className="text-sm text-charcoal/60">
                            {format(new Date(tx.created_at), 'd MMM yyyy, HH:mm', { locale: tr })}
                          </p>
                        </div>
                      </div>
                      <span className={`font-semibold ${tx.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.points > 0 ? '+' : ''}{tx.points} puan
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-charcoal/60">
                  <Award className="w-12 h-12 mx-auto mb-4 opacity-30" />
                  <p>Henüz puan işleminiz yok</p>
                  <p className="text-sm mt-1">Sipariş vererek puan kazanmaya başlayın!</p>
                </div>
              )}
            </motion.div>
          </div>

          {/* Right Column */}
          <div className="lg:col-span-1 space-y-6">
            {/* How it works */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              <h2 className="font-playfair text-xl font-semibold text-charcoal mb-6">
                Nasıl Çalışır?
              </h2>
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-terracotta/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-terracotta font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-medium text-charcoal">Sipariş Verin</p>
                    <p className="text-sm text-charcoal/60">Her €1 harcama = 1 puan</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-terracotta/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-terracotta font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-medium text-charcoal">Puan Biriktirin</p>
                    <p className="text-sm text-charcoal/60">Minimum 100 puan ile kullanın</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-8 h-8 bg-terracotta/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-terracotta font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-medium text-charcoal">İndirim Kazanın</p>
                    <p className="text-sm text-charcoal/60">100 puan = €10 indirim</p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* All Tiers */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl p-6 shadow-sm"
            >
              <h2 className="font-playfair text-xl font-semibold text-charcoal mb-6">
                Üyelik Seviyeleri
              </h2>
              <div className="space-y-3">
                {tiers?.tiers.map((tier) => {
                  const Icon = getTierIcon(tier.name);
                  const isCurrentTier = currentTier?.name === tier.name;
                  return (
                    <div
                      key={tier.name}
                      className={`flex items-center justify-between p-3 rounded-xl transition-all ${
                        isCurrentTier 
                          ? 'bg-basil/10 border-2 border-basil' 
                          : 'bg-stone-50 border-2 border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${getTierColor(tier.name)}`}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className={`font-medium ${isCurrentTier ? 'text-basil' : 'text-charcoal'}`}>
                            {tier.name}
                          </p>
                          <p className="text-xs text-charcoal/60">
                            {tier.min_orders}+ sipariş
                          </p>
                        </div>
                      </div>
                      <span className={`text-sm font-medium ${isCurrentTier ? 'text-basil' : 'text-charcoal/60'}`}>
                        {tier.bonus_multiplier}x puan
                      </span>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Loyalty;
